<template>
	<Application :loading="loading" v-model:errorTitle="errorTitle" :errorDetail="errorDetail">
		<template #navbar v-if="!addNewProviderClicked">
			<v-text-field variant="outlined" density="compact" clearable hide-details
				id="search"
				prepend-inner-icon="mdi-magnify"
				placeholder='Search'
				style="flex-grow: 1;"
				v-model="searchString"
				@keyup.enter="getServiceProviders()"
				data-cy="search-field"
				@click:clear="clearSearch()"
			/>

			<v-btn id="btnCreate" class="gradientButton" elevation="0" size="x-small" @click="addNewProvider">
				<v-icon>mdi-plus</v-icon>
				<span class="d-none d-md-inline">{{$t('text.addServiceProvider')}}</span>
			</v-btn>

			<FilterMenu v-model="filter" @clear="clearFilter" @apply="applyFilter">
				<FilterFieldWrap>
					<div>{{$t('text.serviceProviderStatus')}}</div>
					<v-select multiple chips hide-details
						ref="vSelectStatus"
						v-model="selectedStatuses"
						:items="statuses"
						:item-title="item => $t('text.' + item.id)"
						:item-value="item => item.id"
						:placeholder="$t('text.allLabel')"
						variant="outlined"
						density="compact"
					>
						<template #selection="{ item }">
							<v-chip :color="item.color" dark>{{$t('text.' + item.id)}}</v-chip>
						</template>
					</v-select>
				</FilterFieldWrap>

				<FilterFieldWrap>
					<div>{{$t('text.serviceType')}}</div>
					<v-combobox multiple chips hide-details closable-chips :return-object="false"
						ref="vSelectCategory"
						:placeholder="$t('text.allLabel')"
						variant="outlined"
						density="compact"
						v-model="selectedProductCategories"
						:items="productCategories"
						:item-title="item => item.title[selectedLocale]"
						:item-value="item => item.value"
					>
						<template #selection="{ item }">
							<v-chip :color="item.color" dark>{{item.fields.title[selectedLocale]}}</v-chip>
						</template>
					</v-combobox>
				</FilterFieldWrap>

				<FilterFieldWrap>
					<div>{{$t('text.clientsTitle')}}</div>
					<v-select multiple chips hide-details
						ref="vSelectMarketplace"
						:placeholder="$t('text.allLabel')"
						variant="outlined"
						density="compact"
						v-model="selectedClients"
						:items="clients"
						:item-title="item => item.fields.title[selectedLocale]"
						:item-value="item => item.sys.id"
					>
						<template #selection="{ item }">
							<v-chip :color="item.color" dark>{{item.fields.title[selectedLocale]}}</v-chip>
						</template>
					</v-select>
				</FilterFieldWrap>
				<IfFeatureFlag flag="FEATURE_TRADE_HUB">
					<FilterFieldWrap v-if="featureEnabled(Features.TRADE_HUB)">
						<div>{{$t('text.providerTypeTitle')}}</div>
						<v-select multiple chips hide-details
							ref="vSelectProviderType"
							:placeholder="$t('text.allLabel')"
							variant="outlined"
							density="compact"
							v-model="providerType"
							:items="providerTypes"
							:item-title="item => item.title"
							:item-value="item => item.id"
						>
							<template #selection="{ item }">
								<v-chip dark>{{item.title}}</v-chip>
							</template>
						</v-select>
					</FilterFieldWrap>
				</IfFeatureFlag>
			</FilterMenu>
		</template>

		<v-card class="tableCard">
			<v-data-table fixed-header
				v-model="selected"
				:headers="headers"
				:items="serviceProviders"
				:items-per-page="limit"
				:mobile-breakpoint="700"
				item-key="name"
				class="elevation-0"
				@update:sort-desc="sortTable($event)"
				:item-value="item => item"
			>
				<template v-slot:item="{ item }">
					<tr @click="showProviderDetail(item)">
						<td>
							{{item.fields.title[selectedLocale]}}
						</td>
						<td v-if="showProviderTypeColumn">
							<v-tooltip location="bottom" :text="item.addl?.providerType === 'to' ? $t('text.contractorFilterTypeTourOperator') : $t('text.serviceProvider')">
								<template #activator="{ props }">
									<v-chip v-bind="props" size="small" :color="item.addl?.providerType === 'to' ? '#1867C0' : '#666'">
										{{ item.addl?.providerType === 'to' ? $t('text.providerTypeAbbreviationTO') : $t('text.providerTypeAbbreviationSP') }}
									</v-chip>
								</template>
							</v-tooltip>
						</td>
						<td>{{ getFirstProductCategory(item) }}</td>
<!--						<td>{{ formatDate(item.sys.createdAt) }}</td>-->
						<td style="line-height: 1;">
							<span v-for="cai of item.fields.clientAssignments.de" :key="cai.sys.id">
								<span v-if="cai && cai.fields && cai.fields.status"
									:class="{
										clientAssignmentChip: true,
										[cai.fields.status.de]: true,
									}"
									:title="cai.fields.status.de"
								>
									{{ getClientId(cai) }}
								</span>
							</span>
						</td>
						<td><ApprovalStatus :status="getClientStatus(item)" /></td>
						<td align="center" style="line-height: 100%;">
							<v-tooltip location="bottom" :text="itemStatusTooltip(item)">
								<template #activator="{ props }">
									<div v-bind="props">
										<span v-show="item.addl?.dataStatus === 'no_user_account'"><v-icon color="#f34545">mdi-account-remove</v-icon></span>
										<span v-show="item.addl?.dataStatus === 'complete'"><v-icon color="#64c823">mdi-check-circle</v-icon></span>
										<span v-show="item.addl?.dataStatus === 'incomplete'"><v-icon color="#ffb400">mdi-alert</v-icon></span>
										<span v-show="!item.addl"><v-icon color="#ffb400">mdi-warning</v-icon></span>
									</div>
								</template>
							</v-tooltip>
						</td>
						<td>
							<v-btn class="blueButtonMin" elevation="0" style="color: white; min-width: 30px;"
								v-if="canOperateAs(item)"
								@click.stop="impersonate(item)"
							>
								<v-icon data-cy="operateAs">mdi-drama-masks</v-icon>
								<!--{{ $t('text.operateAs') }}-->
							</v-btn>
						</td>
					</tr>
				</template>
				<template #bottom>
					<TablePaginator class="TablePaginator" :limit="limit" :offset="offset" :total="totalForPaginator" :loadingTotal="totalLoading" @update:offset="updateOffset" :results="serviceProviders" />
				</template>
			</v-data-table>
		</v-card>
	</Application>
</template>

<script>
import Loading from 'vue-loading-overlay'
import ApprovalStatus from '@/components/common/ApprovalStatus.vue'
import Alert from '@/components/common/Alert.vue'
import TablePaginator from '@/components/common/tables/TablePaginator.vue'
import Common from '@/mixins/Common.vue'
import Application from '../Application.vue'
import FilterMenu from '@/components/common/FilterMenu.vue'
import FilterFieldWrap from '@/components/common/FilterFieldWrap.vue'
import { Features } from '../../../../../shared/features'
import IfFeatureFlag from "@/components/ifFeatureFlag/IfFeatureFlag.vue"

export default {
	components: { Loading, ApprovalStatus, Alert, TablePaginator, Application, FilterMenu, FilterFieldWrap, IfFeatureFlag },
	mixins: [ Common ],
	data() { return {
		loading: false,
		offset: this.$store.state.offset,
		limit: 15,
		searchSubmitted: false,
		errorTitle: '',
		errorDetail: '',
		selected: [],
		singleSelect: '',
		serviceProviders: [],
		total: null,
		totalLoading: false,
		totalRequestId: 0,

		searchString: this.$store.state.searchString,
		addNewProviderClicked: false,
		filter: this.$store.state.filter,
		statuses: [
			{ id: 'pending', color: '#ff9e21', dark: true },
			{ id: 'reapprove', color: '#ffb400' },
			{ id: 'approved', color: '#64c823', dark: true },
			{ id: 'deactivated', color: '#f34545', dark: true },
			{ id: 'declined', color: '#f34545', dark: true },
		],
		selectedStatuses: this.$store.state.filter ? this.$store.state.filter.statuses : [],
		validFromDate: this.$store.state.filter ? this.$store.state.filter.validFromDate : '',
		validToDate: this.$store.state.filter ? this.$store.state.filter.validToDate : '',
		productCategories: [],
		selectedProductCategories: this.$store.state.filter ? this.$store.state.filter.productCategories : [],
		clients: [],
		selectedClients: this.$store.state.filter ? this.$store.state.filter.selectedClients : [],
		showFromDate: false,
		showToDate: false,
		isNegative:  this.$store.state.filter ? this.$store.state.filter.isNegative : false,
		dropdownOpen: false,
		Features,
		providerTypes: [{ id: 'sp', title: this.$t('text.serviceProvider') }, { id: 'to', title: this.$t('text.contractorFilterTypeTourOperator') }],
		providerType: this.$store.state.filter ? this.$store.state.filter.providerType : ['sp'],
		pageIndex: 0,
		pageItems: [],
		pageCursors: [],
		pageNextCursors: [],
		currentNextCursor: null
	}},
	computed: {
		showProviderTypeColumn() {
			return this.featureEnabled(Features.TRADE_HUB) && Array.isArray(this.providerType) && this.providerType.length > 1
		},
		headers () {
			const h = [
				{ title: this.$t('text.providerName'), key: "provider_name", width: this.showProviderTypeColumn ? '35%' : '40%', sortable: false, cellClass: 'truncate' },
				{ title: this.$t('text.category'), key: "category", width: '15%', sortable: false, cellClass: 'truncate' },
//				{ title: this.$t('text.registrationDate'), key: "registration_date", width: '10%', sortable: false, cellClass: 'truncate' },
				{ title: this.$t('text.clientAssignments'), key: "client_assignments", width: '20%', sortable: false },
				{ title: this.$t('text.approval'), key: "status", width: '15%', sortable: false },
				{ title: this.$t('text.status'), key: "data_status", width: '50px', align: 'center', sortable: false },
				{ title: '', key: "operate_as", width: '10%', sortable: false },
			]
			if (this.showProviderTypeColumn) {
				h.splice(1, 0, { title: this.$t('text.providerTypeTitle'), key: "provider_type", width: '70px', sortable: false, cellClass: 'truncate' })
			}
			return h
		},
		validFromText() {
			if (this.validFromDate === '' || this.isNegative) {
				return this.$t('text.validFrom')
			}
			else return this.validFromDate
		},
		validToText() {
			if (this.validToDate === '' || this.isNegative) {
				return this.$t('text.validUntil')
			}
			else return this.validToDate
		},
		totalForPaginator() {
			return typeof this.total === 'number' ? this.total : 99999
		},
	},
	watch: {
		'$store.state.selectedLocale': function() {
			this.selectedLocale = this.$store.state.selectedLocale
		},
		providerType: {
			deep: true,
			handler(newVal, oldVal) {
				if (newVal.length === 0) {
					this.$nextTick(() => {
						this.providerType = [...oldVal]
					})
				}
			}
		}
	},
	methods: {
		resetCursorState() {
			this.pageIndex = 0
			this.pageItems = []
			this.pageCursors = []
			this.pageNextCursors = []
			this.currentNextCursor = null
			this.offset = 0
			this.total = null
			this.totalLoading = false
		},
		updateOffset(newOffset) {
			if (newOffset > this.offset) {
				this.goNextCursorPage()
			} else if (newOffset < this.offset) {
				this.goPreviousCursorPage()
			}
		},
		itemStatusTooltip(item) {
			if (item.addl?.dataStatus === 'no_user_account') {
				return this.$t('text.noUserAccount')
			} else if (item.addl?.dataStatus === 'complete') {
				return this.$t('text.complete')
			} else if (item.addl?.dataStatus === 'incomplete') {
				return this.$t('text.incomplete')
			} else {
				return this.$t('text.incomplete')
			}
		},
		userHasAccount(item) {
			return item?.fields?.userAccount?.de?.sys?.id?.length
		},
		getFirstProductCategory(serviceProvider) {
			const currentCA = serviceProvider.fields.clientAssignments?.de?.find(x => x?.fields?.client?.de?.sys?.id === this.$store.state.selectedClient.sys.id)
			return currentCA?.fields?.productCategories?.de?.[0]?.fields?.title?.[this.selectedLocale]
		},
		getClientStatus(serviceProvider) {
			const currentCA = serviceProvider.fields.clientAssignments.de.find(x => x?.fields?.client?.de?.sys?.id === this.$store.state.selectedClient.sys.id)
			return currentCA?.fields?.status?.de
		},
		canOperateAs(serviceProvider) {
			const currentCA = serviceProvider.fields.clientAssignments.de.find(x => x?.fields?.client?.de?.sys?.id === this.$store.state.selectedClient.sys.id)
			if (currentCA?.fields?.isHomebase?.de === true && serviceProvider.fields.userAccount?.de?.sys?.id) {
				return true
			}
			return false
		},
		getClientId(cai) {
			return cai?.fields?.client?.de?.fields?.clientId?.de ? cai.fields.client.de.fields.clientId.de : ''
		},
		addNewProvider() {
			this.addNewProviderClicked = true
			this.showNewProvider()
		},
		commitFilterSafe(filter) {
			const plainFilter = filter == null ? null : JSON.parse(JSON.stringify(filter))
			this.$store.commit('setFilter', plainFilter)
		},
		applyFilter() {
			const hasFilter =
				this.selectedStatuses.length > 0 ||
				this.validFromDate.length > 0 ||
				this.validToDate.length > 0 ||
				this.selectedProductCategories.length > 0 ||
				this.selectedClients.length > 0 ||
				this.isNegative ||
				(this.featureEnabled(Features.TRADE_HUB) && this.providerType?.length > 0)

			if (hasFilter) {
				this.filter = {
					statuses: this.selectedStatuses,
					validFromDate: this.validFromDate,
					validToDate: this.validToDate,
					isNegative: this.isNegative,
					productCategories: this.selectedProductCategories,
					selectedClients: this.selectedClients
				}

				if (this.featureEnabled(Features.TRADE_HUB)) {
					this.filter.providerType = this.providerType
				}

				this.offset = 0
				this.commitFilterSafe(this.filter)
				this.getServiceProviders()
			}
			else {
				this.clearFilter()
			}
		},
		clearFilter() {
			this.selectedStatuses = []
			this.validFromDate = ''
			this.validToDate = ''
			this.isNegative = false
			this.selectedProductCategories = []
			this.selectedClients = []
			this.providerType = ['sp']

			if (this.featureEnabled(Features.TRADE_HUB)) {
				this.filter = { providerType: this.providerType }
			} else {
				this.filter = null
			}
			this.commitFilterSafe(this.filter)
			this.offset = 0
			this.getServiceProviders()
		},
		toggleDatePicker(pickerToToggle) {
			if (pickerToToggle === 'from') {
				this.showToDate = false
				this.showFromDate = true
			} else {
				this.showFromDate = false
				this.showToDate = true
			}
		},
		resetDates() {
			this.validToDate = ''
			this.showFromDate = false
		},
		closeDropDown(ref) {
			if (!this.dropdownOpen) {
				this.$refs[ref].focus()
				this.dropdownOpen = true
			} else {
				this.$refs[ref].blur()
				this.dropdownOpen = false
			}
		},
		async getProductCategories() {
			try {
				const productCategories = this.$store.state.selectedClient.fields.productCategories.de
				if (productCategories) { productCategories.sort(this.compare) }

				if (productCategories?.length > 0) {
					for (let productCategory of productCategories) {
						if (productCategory.fields?.subProductCategories?.de) {productCategory.fields.subProductCategories.de.sort(this.compare)}
					}
				}
				this.productCategories = productCategories.map(item => ({
					title: item.fields?.title ?? {},
					value: item.sys.id,
				}))//.slice(0, 10)
			}
			catch (error) {
				this.showError(error)
			}
		},
		async getMarketplaceClientsForClient(client) {
			let clients = []
			const clientSysId = client.sys.id
			let marketplaces = await this.$httpGet('/marketplace?clientSysId=' + clientSysId)
			let mp = marketplaces?.[0]
			let lookup = {}
			for (let client of mp.fields.externalClients.de) {
				clients.push(client)
				lookup[client.sys.id] = client
			}
			// add homeClient if it wasnt in the external clients
			if (!lookup[mp.fields.homeClient.de.sys.id])
				clients.push(mp.fields.homeClient.de)
			this.clients = clients
		},
		async sortTable(event) {
			let sortMode = event[0]
			await this.getServiceProviders(sortMode)
		},
		async showProviderDetail(item) {
			await this.$store.commit('setSelectedServiceProvider', item)
			this.$router.push('/businessProfile')
		},
		async showNewProvider() {
			await this.$store.commit('setSelectedServiceProvider', null)
			await this.$store.commit('setPeakProducts', null)
			this.$router.push('/businessProfile')
		},
		buildServiceProvidersUrl(cursor, sortMode) {
			const clientId = this.$store.state.selectedClient.sys.id

			let url = `/serviceproviders?clientId=${clientId}&cursor=${cursor}&limit=${this.limit}`

			if (this.$store.state.isImpersonation === true && this.$store.state.selectedServiceProvider.fields.tags?.de) {
				url += `&tags=${this.$store.state.selectedServiceProvider.fields.tags.de}`
			}

			if (
				this.userIsOperator &&
				!this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de.find(x => x === 'ALL')
			) {
				url += `&tags=${this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de}`
			}

			if (this.$store.state.filter) {
				url += `&filter=${encodeURIComponent(JSON.stringify(this.$store.state.filter))}`
			}

			if (sortMode != undefined) {
				url += `&sortMode=${sortMode}&selectedLocale=${this.selectedLocale}`
			}

			return url
		},
		async fetchServiceProvidersPage(cursor, sortMode) {
			const url = this.buildServiceProvidersUrl(cursor, sortMode)
			const res = await this.$httpGet(url)

			return {
				items: res.serviceProviders || [],
				nextCursor: res.nextCursor ?? null
			}
		},
		buildSearchBody(cursor) {
			const clientId = this.$store.state.selectedClient.sys.id

			const body = {
				clientId,
				contentType: 'serviceProvider',
				searchString: this.searchString,
				cursor,
				limit: this.limit,
				filter: this.filter,
				homebaseOnly: false,
				tagMode: 'SEE'
			}

			if (
				this.userIsOperator &&
				this.$store.state.isImpersonation === false &&
				!this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de.find(x => x === 'ALL')
			) {
				body.tags = this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de
			}

			return body
		},
		async fetchSearchPage(cursor) {
			const url = '/search'
			const requestBody = this.buildSearchBody(cursor)
			const res = await this.$httpPost(url, requestBody)

			return {
				items: res?.serviceProviders || [],
				nextCursor: res.nextCursor ?? null
			}
		},
		getListTags() {
			let tags = []
			if (this.$store.state.isImpersonation === true && this.$store.state.selectedServiceProvider.fields.tags?.de) {
				tags = this.$store.state.selectedServiceProvider.fields.tags.de
			}
			if (
				this.userIsOperator &&
				!this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de.find(x => x === 'ALL')
			) {
				tags = this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de
			}
			return tags
		},
		getSearchTags() {
			if (
				this.userIsOperator &&
				this.$store.state.isImpersonation === false &&
				!this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de.find(x => x === 'ALL')
			) {
				return this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de
			}
			return []
		},
		buildCountBody(isSearchMode) {
			return {
				clientId: this.$store.state.selectedClient.sys.id,
				searchString: isSearchMode ? this.searchString : '',
				filter: this.filter,
				tags: isSearchMode ? this.getSearchTags() : this.getListTags(),
				tagMode: 'SEE'
			}
		},
		async loadServiceProviderCount(isSearchMode) {
			const requestId = ++this.totalRequestId
			this.total = null
			this.totalLoading = true

			try {
				const res = await this.$httpPost('/serviceproviders/count', this.buildCountBody(isSearchMode))
				if (requestId !== this.totalRequestId) {
					return
				}
				this.total = typeof res?.total === 'number' ? res.total : null
			}
			catch (_error) {
				if (requestId !== this.totalRequestId) {
					return
				}
				this.total = null
			}
			finally {
				if (requestId === this.totalRequestId) {
					this.totalLoading = false
				}
			}
		},
		async getServiceProviders(sortMode) {
			await this.$store.commit('setPeakProducts', null)
			await this.$store.commit('setSelectedServiceProvider', null)

			if (this.searchString) {
				return this.getSearchedServiceProviders()
			}

			this.loading = true
			try {
				this.resetCursorState()
				this.loadServiceProviderCount(false)
				await this.$store.commit('setOffset', this.offset)

				const cursor = 0
				const { items, nextCursor } = await this.fetchServiceProvidersPage(cursor, sortMode)

				this.serviceProviders = items

				this.pageIndex = 0
				this.pageItems[0] = items
				this.pageCursors[0] = cursor
				this.pageNextCursors[0] = nextCursor
				this.currentNextCursor = nextCursor
			} catch (error) {
				if (error.response?.status == 400) {
					this.serviceProviders = []
				}
				else {
					this.errorTitle = this.$t('text.ERROR')
					this.errorDetail = error.response?.error ?? error
				}
			}
			this.loading = false
		},
		async getSearchedServiceProviders() {
			this.loading = true
			try {
				this.resetCursorState()
				this.loadServiceProviderCount(true)
				await this.$store.commit('setOffset', this.offset)
				await this.$store.commit('setSearchString', this.searchString)

				const cursor = 0
				const { items, nextCursor } = await this.fetchSearchPage(cursor)

				this.serviceProviders = items

				this.pageIndex = 0
				this.pageItems[0] = items
				this.pageCursors[0] = cursor
				this.pageNextCursors[0] = nextCursor
				this.currentNextCursor = nextCursor
			} catch (error) {
				if (error.response?.status == 400) {
					this.serviceProviders = []
				}
				else {
					this.errorTitle = this.$t('text.ERROR')
					this.errorDetail = error.response?.error ?? error
				}
			}
			this.loading = false
		},
		async clearSearch() {
			this.offset = 0
			this.searchString = ''
			await this.$store.commit('setSearchString', null)
			
			this.getServiceProviders()
		},
		async goNextCursorPage() {
			if (this.currentNextCursor == null) {
				return
			}

			const targetIndex = this.pageIndex + 1
			if (this.pageItems[targetIndex]) {
				this.pageIndex = targetIndex
				this.serviceProviders = this.pageItems[targetIndex]
				this.offset = this.pageIndex * this.limit
				this.currentNextCursor = this.pageNextCursors[targetIndex] ?? null
				return
			}

			this.loading = true
			try {
				const cursor = this.currentNextCursor

				const { items, nextCursor } = this.searchString
					? await this.fetchSearchPage(cursor)
					: await this.fetchServiceProvidersPage(cursor)

				this.pageIndex = targetIndex
				this.offset = this.pageIndex * this.limit
				this.serviceProviders = items

				this.pageItems[targetIndex] = items
				this.pageCursors[targetIndex] = cursor
				this.pageNextCursors[targetIndex] = nextCursor
				this.currentNextCursor = nextCursor
			} catch (error) {
				if (error.response?.status == 400) {
					this.serviceProviders = []
				} else {
					this.errorTitle = this.$t('text.ERROR')
					this.errorDetail = error.response?.error ?? error
				}
			}
			this.loading = false
		},
		goPreviousCursorPage() {
			if (this.pageIndex === 0) {
				return
			}

			const targetIndex = this.pageIndex - 1
			const items = this.pageItems[targetIndex]

			if (!items) {
				return
			}

			this.pageIndex = targetIndex
			this.offset = this.pageIndex * this.limit
			this.serviceProviders = items
			this.currentNextCursor = this.pageNextCursors[targetIndex] ?? null
		},
	},
	created() {
		if (this.featureEnabled(Features.TRADE_HUB)) {
			this.filter = this.$store.state.filter ?? {
				providerType: this.providerType
			}
			this.commitFilterSafe(this.filter)
		} else {
			this.filter = this.$store.state.filter
		}
	},
	async mounted() {
		this.getServiceProviders()

		await this.getProductCategories()
		await this.getMarketplaceClientsForClient(this.$store.state.selectedClient)
	},
}
</script>

<style scoped>
input::placeholder { color: red!important; margin-left: 20px; }
.v-data-table__mobile-row { word-break: break-all; }
.v-badge--bordered .v-badge__badge::after { width: 14px !important; height: 14px !important; }
.v-badge__badge {
	width: 14px !important;
	height: 14px !important;
	min-width: 14px !important;
	max-width: 14px !important;
}
.v-tooltip__content {
	color: black !important;
	opacity: 1 !important;
	padding: 5px !important;
	z-index: 999 !important;
	max-height: 28px !important;
}
td { font-size: 12pt !important; }
.TablePaginator { text-align: right; background: #f4f4f4; border-top: 1px solid #ddd; padding: 10px; }
</style>
